// src/services/firebaseService.js
import { 
    collection, 
    addDoc, 
    getDocs,
    getDoc,
    doc,
    deleteDoc, 
    updateDoc,
    query, 
    where 
} from "firebase/firestore";
import { db } from "../firebase/config";

export const firebaseService = {
    // Un álbum por ID (una sola lectura)
    async getAlbum(albumId) {
        const snap = await getDoc(doc(db, "albumes", albumId));
        return snap.exists() ? { id: snap.id, ...snap.data(), fotos: snap.data().fotos || [] } : null;
    },

    // Obtener álbumes por deporte (incluye campo orden)
    async getAlbumesByDeporte(deporteId) {
      try {
          console.log(`📡 [firebaseService] Obteniendo álbumes para: ${deporteId}`);
          
          const q = query(
              collection(db, "albumes"),
              where("deporte", "==", deporteId)
          );
          
          const querySnapshot = await getDocs(q);
          const albumes = [];
          
          querySnapshot.forEach((doc) => {
              const data = doc.data();
              albumes.push({
                  id: doc.id,
                  orden: data.orden !== undefined ? data.orden : 999,
                  nombre: data.nombre,
                  descripcion: data.descripcion,
                  deporte: data.deporte,
                  fechaCreacion: data.fechaCreacion,
                  fotos: data.fotos || []
              });
          });
          
          console.log(`✅ [firebaseService] Encontrados ${albumes.length} álbumes`);
          
          // Ordenar por el campo "orden"
          albumes.sort((a, b) => {
              const ordenA = a.orden !== undefined ? a.orden : 999;
              const ordenB = b.orden !== undefined ? b.orden : 999;
              return ordenA - ordenB;
          });
          
          return albumes;
      } catch (error) {
          console.error("❌ [firebaseService] Error obteniendo álbumes:", error);
          return [];
      }
  },
    // Guardar foto de bio en Firebase
    async saveBioPhoto(photoData) {
        try {
            console.log("💾 Guardando foto de bio:", photoData.nombre);
            
            const docRef = await addDoc(collection(db, "bio_fotos"), {
                url: photoData.url,
                publicId: photoData.publicId,
                nombre: photoData.nombre,
                descripcion: photoData.descripcion || '',
                fechaSubida: new Date().toISOString()
            });
            
            console.log("✅ Foto de bio guardada con ID:", docRef.id);
            return { 
                success: true, 
                id: docRef.id,
                data: photoData
            };
        } catch (error) {
            console.error("❌ Error guardando foto de bio:", error);
            return { success: false, error };
        }
    },

    // Obtener fotos de bio desde Firebase
    async getBioFotos() {
        try {
            console.log("📡 Obteniendo fotos de bio...");
            
            const querySnapshot = await getDocs(collection(db, "bio_fotos"));
            const fotos = [];
            
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                fotos.push({
                    id: doc.id,
                    url: data.url,
                    descripcion: data.descripcion || '',
                    nombre: data.nombre || 'Foto',
                    fechaSubida: data.fechaSubida,
                    publicId: data.publicId,
                    orden: data.orden
                });
            });

            // Orden manual primero; las nuevas (sin orden) van al inicio, por fecha
            return fotos.sort((a, b) =>
                (a.orden ?? -1) - (b.orden ?? -1) ||
                new Date(b.fechaSubida) - new Date(a.fechaSubida)
            );
        } catch (error) {
            console.error("❌ Error obteniendo fotos de bio:", error);
            return [];
        }
    },

    // Guardar foto de perfil
    async saveFotoPerfil(photoData) {
        try {
            console.log("💾 Guardando foto de perfil");
            
            // Primero borrar cualquier foto de perfil existente
            const querySnapshot = await getDocs(collection(db, "foto_perfil"));
            const deletePromises = [];
            
            querySnapshot.forEach((doc) => {
                deletePromises.push(deleteDoc(doc.ref));
            });
            
            await Promise.all(deletePromises);
            
            // Guardar nueva foto
            const docRef = await addDoc(collection(db, "foto_perfil"), {
                url: photoData.url,
                publicId: photoData.publicId,
                fechaSubida: new Date().toISOString()
            });
            
            console.log("✅ Foto de perfil guardada");
            return { success: true, id: docRef.id };
        } catch (error) {
            console.error("❌ Error guardando foto de perfil:", error);
            return { success: false, error };
        }
    },

    // Obtener foto de perfil
    async getFotoPerfil() {
        try {
            const querySnapshot = await getDocs(collection(db, "foto_perfil"));
            
            if (!querySnapshot.empty) {
                const doc = querySnapshot.docs[0];
                return {
                    id: doc.id,
                    ...doc.data()
                };
            }
            
            return null;
        } catch (error) {
            console.error("❌ Error obteniendo foto de perfil:", error);
            return null;
        }
    },

    // Eliminar foto de bio
    async deleteBioPhoto(photoId) {
        try {
            console.log(`🗑️ Eliminando foto de bio: ${photoId}`);
            
            await deleteDoc(doc(db, "bio_fotos", photoId));
            
            console.log('✅ Foto de bio eliminada');
            return { success: true };
        } catch (error) {
            console.error('❌ Error eliminando foto de bio:', error);
            return { success: false, error };
        }
    },

    // Eliminar foto de perfil
    async deleteFotoPerfil() {
        try {
            console.log('🗑️ Eliminando foto de perfil...');
            
            const querySnapshot = await getDocs(collection(db, "foto_perfil"));
            const deletePromises = [];
            
            querySnapshot.forEach((doc) => {
                deletePromises.push(deleteDoc(doc.ref));
            });
            
            await Promise.all(deletePromises);
            
            console.log('✅ Foto de perfil eliminada');
            return { success: true };
        } catch (error) {
            console.error('❌ Error eliminando foto de perfil:', error);
            return { success: false, error };
        }
    },

    // Actualizar orden de fotos favoritas.
    // Antes borraba y recreaba todos los docs: si fallaba a medias se perdían fotos,
    // los IDs cambiaban (y "eliminar" dejaba de funcionar) y al recargar volvía el orden por fecha.
    async updateBioPhotosOrder(orderedFotos) {
        try {
            await Promise.all(orderedFotos.map((foto, orden) =>
                updateDoc(doc(db, "bio_fotos", foto.id), { orden })
            ));
            return { success: true };
        } catch (error) {
            console.error('❌ Error actualizando orden:', error);
            return { success: false, error };
        }
    }
};